<script lang="ts">
	import type { Subscription } from 'rxjs';
	import { deregisterModal, goto, registerModal } from '$lib/serv/modal';
	import { obsEqualLastUrl } from '$lib/serv/nav';
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { fade, fly } from 'svelte/transition';
	import ToolTip, { ToolPosition } from './ToolTip.svelte';

	const dispatch = createEventDispatcher();
	const dispatchClose = () => dispatch('close');

	let _close: (() => void) | string | undefined = undefined;
	let subLastUrl: Subscription;

	export let size: 'small' | 'medium' | 'medium-large' | 'large' = 'medium';
	export let hrefClose = '';
	/** Test id */
	export let id = '';
	/** Show nested modal */
	export let child = false;
	
	/**
	 * External hook for closing a modal the way the modal intends to close
	 */
	export const close = () => {
		typeof _close === 'string' ? goto(_close) : _close?.();
	};

	$: {
		subLastUrl?.unsubscribe();
		if (hrefClose) {
			subLastUrl = obsLastUrl(hrefClose);
		} else {
			_close = dispatchClose;
			// console.log('Modal back is a function')
		}
	}

	function obsLastUrl(href: string) {
		return obsEqualLastUrl(href).subscribe(equal => {
			if (equal) {
				_close = () => window.history.back();
				// console.log('Modal back is native')
			} else {
				_close = href;
				// console.log('Modal back is an href')
			}
		});
	}

	let modal: HTMLDivElement;
	const closer = () => hrefClose ? goto(hrefClose) : dispatchClose();

	onMount(() => {
		registerModal(closer);
	})

	onDestroy(() => {
		subLastUrl?.unsubscribe();
		deregisterModal(closer);
	});

	const handle_keydown = (e:KeyboardEvent) => {
		if (e.key === 'Escape') {
			closer();
			return;
		}

		if (e.key === 'Tab') {
			// trap focus
			const nodes = modal.querySelectorAll('*');
			const nodelist = Array.from(nodes) as HTMLElement[];
			const tabbable = nodelist.filter(n => n.tabIndex >= 0);

			let index = document.activeElement && tabbable.indexOf(document.activeElement as HTMLElement) || -1;
			if (index === -1 && e.shiftKey) index = 0;

			index += tabbable.length + (e.shiftKey ? -1 : 1);
			index %= tabbable.length;

			tabbable[index].focus();
			e.preventDefault();
		}
	};

	// const previously_focused = typeof document !== 'undefined' && document.activeElement as HTMLElement;
	// if (previously_focused) onDestroy(() => {
	// 	// return focus to the previous element when the modal was opened
	// 	previously_focused.focus();
	// });
</script>

<svelte:window on:keydown={handle_keydown} />
<svelte:body class="no-scroll" />

{#if typeof _close !== 'string'}
<button class="modal-background" on:click={_close} transition:fade={{ duration: 200 }}></button>
{:else}
<!-- svelte-ignore a11y-missing-content -->
<a href={_close} class="modal-background" transition:fade={{ duration: 200 }} data-sveltekit-noscroll></a>
{/if}


<!-- <div class="modal {size}" role="dialog" aria-modal="true" bind:this={modal} transition:fly|global="{{ y: 200, duration: 300 }}"> -->
<div class="modal {size} max-w-1600" class:has-child={child} role="dialog" aria-modal="true" bind:this={modal} in:fly={{ y: -50, duration: 200 }} out:fly={{ y: 50, duration: 200 }} data-testid={`modal-${id}`}>
	<div class="header" class:border={$$slots.header}>
		{#if $$slots.header}
			<div class="headline-m bold m-0"><slot name="header" /></div>
		{/if}
		{#if typeof _close === 'string'}
			<a href={_close} class="close" data-testid="close" data-sveltekit-noscroll>
				<span class="flex icon-close-28"></span>
				<ToolTip message="Close" pos={ToolPosition.Left} />
			</a>
		{:else}
			<!-- svelte-ignore a11y-autofocus -->
			<button class="close" on:click={_close} on:keypress data-testid="close">
				<span class="flex icon-close-28"></span>
				<ToolTip message="Close" pos={ToolPosition.Left} />
			</button>
		{/if}
		
	</div>
	<div class="body" id="modal-body"> <!-- id needed for inifinteScroll pagination on item search -->
		<slot />
	</div>
	{#if $$slots.footer}
		<div class="footer">
			<slot name="footer" />
		</div>
	{/if}
</div>

{#if child && $$slots.modal}
	<slot name="modal" />
{/if}

<!-- {#if $$slots.child}
	<svelte:self size="large" on:close={() => showchild = false}>
		<slot name="child" />
	</svelte:self>
{/if} -->
